import { confirmDialog as confirm } from "@/components/ConfirmProvider";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  createAgency, getAgencyOverview, listClients, addClient, deleteClient,
  agencyTalent, listSubmissions, submitCandidate, updateSubmission,
  agencyOpenJobs, submitToJob,
} from "@/lib/agencyApi";
import {
  Briefcase, Building2, Send, Users, ArrowRight, Loader2, Plus, X, Trash2,
  CheckCircle2, Search, Copy, Check, Link2, MapPin,
} from "lucide-react";

const STAGES = ["submitted", "shortlisted", "interview", "offer", "placed", "rejected"];
const STAGE_STYLE = {
  submitted: "bg-stone-100 text-stone-700",
  shortlisted: "bg-blue-100 text-blue-700",
  interview: "bg-amber-100 text-amber-700",
  offer: "bg-purple-100 text-purple-700",
  placed: "bg-emerald-100 text-emerald-700",
  rejected: "bg-red-100 text-red-600",
};

function ScorePill({ score }) {
  const c = score >= 80 ? "#15803D" : score >= 60 ? "#D97706" : "#C2410C";
  return <span className="font-mono text-sm font-bold" style={{ color: c }}>{score}</span>;
}

/* ─────────── Onboarding ─────────── */
export function AgencyOnboarding() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [name, setName] = useState("");
  const [website, setWebsite] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { setErr("Agency name is required."); return; }
    setBusy(true); setErr("");
    try {
      await createAgency({ name: name.trim(), website: website.trim() || undefined });
      await refresh();
      navigate("/agency", { replace: true });
    } catch (e) { setErr(e?.response?.data?.detail || "Could not create workspace."); setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] py-14">
      <div className="container-x max-w-xl">
        <span className="inline-flex p-3 rounded-2xl bg-stone-900 text-white"><Briefcase size={22} /></span>
        <h1 className="font-display text-4xl md:text-5xl mt-4 leading-tight">Set up your <span className="italic text-stone-500">agency workspace.</span></h1>
        <p className="mt-3 text-stone-600">Recruit for multiple clients, source pre-scored candidates, and submit them with proof.</p>
        <form onSubmit={submit} className="mt-8 space-y-4">
          <input autoFocus value={name} onChange={(e) => setName(e.target.value)} data-testid="ag-ob-name" placeholder="Agency name (e.g. Apex Recruiters)" className="w-full px-5 py-4 rounded-2xl bg-white border-2 border-stone-200 focus:outline-none focus:border-stone-900 text-base" />
          <input value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="Website (optional)" className="w-full px-5 py-3.5 rounded-2xl bg-white border-2 border-stone-200 focus:outline-none focus:border-stone-900 text-sm" />
          {err && <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-4 py-2">{err}</p>}
          <button type="submit" disabled={busy} data-testid="ag-ob-submit" className="btn-primary w-full justify-center disabled:opacity-60">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <>Create workspace <ArrowRight size={16} /></>}
          </button>
        </form>
      </div>
    </div>
  );
}

/* ─────────── Dashboard ─────────── */
export function AgencyDashboard() {
  const [ov, setOv] = useState(null);
  useEffect(() => { getAgencyOverview().then(({ data }) => setOv(data)).catch(() => {}); }, []);
  return (
    <div className="container-x py-10 md:py-14">
      <span className="overline">Agency workspace</span>
      <h1 className="font-display text-4xl md:text-5xl mt-2">Your placements at a glance.</h1>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
        <Stat icon={Building2} label="Clients" value={ov?.clients ?? 0} to="/agency/clients" accent="#0a66c2" />
        <Stat icon={Send} label="Submissions" value={ov?.submissions ?? 0} to="/agency/submissions" accent="#C2410C" />
        <Stat icon={CheckCircle2} label="Placed" value={ov?.placed ?? 0} to="/agency/submissions" accent="#15803D" />
        <Stat icon={Sparkles2} label="Credits" value={ov?.credits ?? 0} to="/agency/talent" accent="#7C3AED" />
      </div>
      <div className="mt-10 rounded-3xl bg-stone-900 text-stone-50 p-8 md:p-10 relative overflow-hidden wsh-grain">
        <div className="absolute inset-0 opacity-40" style={{ background: "radial-gradient(500px 320px at 90% 10%, rgba(217,119,6,0.4), transparent 60%)" }} />
        <div className="relative grid grid-cols-1 md:grid-cols-3 gap-8 items-center">
          <div className="md:col-span-2">
            <span className="overline" style={{ color: "rgba(253,251,247,0.55)" }}>Source smarter</span>
            <h3 className="font-display text-3xl mt-2">Submit candidates with a <span className="italic text-amber-300">verified score.</span></h3>
            <p className="mt-3 text-stone-300 max-w-xl">Browse candidates pre-ranked by Wishire Score, then submit the best to a client — your client sees objective proof, not just a résumé.</p>
          </div>
          <a href="/agency/talent" className="inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-full bg-amber-300 text-stone-900 hover:bg-amber-200 transition-colors font-medium">Browse talent <ArrowRight size={16} /></a>
        </div>
      </div>
    </div>
  );
}
function Sparkles2(props){ return <Users {...props} />; }
function Stat({ icon: Icon, label, value, to, accent }) {
  return (
    <a href={to} className="group rounded-3xl border border-stone-200 bg-white p-6 hover:-translate-y-1 hover:shadow-[0_24px_60px_-30px_rgba(28,25,23,0.3)] transition-all">
      <span className="inline-flex p-2.5 rounded-xl mb-4" style={{ background: `${accent}15`, color: accent }}><Icon size={20} /></span>
      <p className="font-display text-4xl">{value}</p>
      <p className="overline mt-1">{label}</p>
    </a>
  );
}

/* ─────────── Clients ─────────── */
export function AgencyClients() {
  const [rows, setRows] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: "", contact_name: "", contact_email: "" });
  const [busy, setBusy] = useState(false);
  const load = () => listClients().then(({ data }) => setRows(data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setBusy(true);
    try { await addClient(form); setForm({ name: "", contact_name: "", contact_email: "" }); setShowAdd(false); load(); }
    finally { setBusy(false); }
  };
  const remove = async (c) => {
    if (!(await confirm({ title: "Please confirm", description: `Delete client "${c.name}" and its submissions?`, danger: true }))) return;
    await deleteClient(c.client_id); load();
  };

  return (
    <div className="container-x py-10 md:py-14">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-8">
        <div>
          <span className="overline">Clients</span>
          <h1 className="font-display text-4xl md:text-5xl mt-2">Companies you recruit for.</h1>
        </div>
        <button onClick={() => setShowAdd(true)} data-testid="ag-add-client" className="btn-primary shrink-0"><Plus size={16} /> Add client</button>
      </div>

      {showAdd && (
        <form onSubmit={add} className="mb-6 rounded-2xl border border-stone-200 bg-white p-5 grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
          <Inp label="Client name *" v={form.name} on={(v) => setForm((f) => ({ ...f, name: v }))} test="ag-client-name" />
          <Inp label="Contact name" v={form.contact_name} on={(v) => setForm((f) => ({ ...f, contact_name: v }))} />
          <Inp label="Contact email" v={form.contact_email} on={(v) => setForm((f) => ({ ...f, contact_email: v }))} />
          <div className="sm:col-span-3 flex gap-2">
            <button type="submit" disabled={busy} className="btn-primary text-sm">{busy ? <Loader2 size={14} className="animate-spin" /> : "Save client"}</button>
            <button type="button" onClick={() => setShowAdd(false)} className="btn-ghost text-sm">Cancel</button>
          </div>
        </form>
      )}

      {rows === null ? <p className="text-stone-500">Loading…</p> : rows.length === 0 ? (
        <div className="rounded-3xl border border-dashed border-stone-300 bg-[#F5F3ED] p-12 text-center text-stone-500">No clients yet. Add the companies you recruit for.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {rows.map((c) => (
            <div key={c.client_id} className="rounded-3xl border border-stone-200 bg-white p-6">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-stone-100 flex items-center justify-center"><Building2 size={18} className="text-stone-600" /></div>
                  <div><p className="font-display text-xl">{c.name}</p><p className="text-xs text-stone-500">{c.contact_name || "—"}</p></div>
                </div>
                <button onClick={() => remove(c)} className="p-1.5 rounded-lg text-stone-400 hover:text-red-600 hover:bg-red-50"><Trash2 size={14} /></button>
              </div>
              <p className="mt-4 text-sm text-stone-600"><Send size={13} className="inline mr-1" /> {c.submission_count} submission(s)</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
function Inp({ label, v, on, test }) {
  return (
    <div>
      <label className="overline">{label}</label>
      <input value={v} onChange={(e) => on(e.target.value)} data-testid={test}
        className="mt-1.5 w-full px-4 py-2.5 rounded-xl bg-white border border-stone-300 text-sm focus:outline-none focus:border-stone-900" />
    </div>
  );
}

/* ─────────── Talent (source + submit) ─────────── */
export function AgencyTalent() {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [clients, setClients] = useState([]);
  const [submitFor, setSubmitFor] = useState(null); // candidate being submitted

  const load = (role) => agencyTalent(role).then(({ data }) => setRows(data)).catch(() => setRows([]));
  useEffect(() => { load(); listClients().then(({ data }) => setClients(data)).catch(() => {}); }, []);

  return (
    <div className="container-x py-10 md:py-14">
      <span className="overline">Talent</span>
      <h1 className="font-display text-4xl md:text-5xl mt-2">Candidates, ranked by Wishire Score.</h1>
      <div className="mt-6 mb-6 relative max-w-md">
        <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400" />
        <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load(q)}
          placeholder="Filter by target role… (Enter)" className="w-full pl-10 pr-4 py-2.5 rounded-full bg-white border border-stone-300 text-sm focus:outline-none focus:border-stone-900" />
      </div>

      {rows === null ? <p className="text-stone-500">Loading…</p> : rows.length === 0 ? (
        <div className="rounded-3xl border border-dashed border-stone-300 bg-[#F5F3ED] p-12 text-center text-stone-500">No scored candidates yet.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {rows.map((c) => (
            <div key={c.user_id} className="rounded-3xl border border-stone-200 bg-white p-6 flex flex-col">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-amber-100 text-amber-800 flex items-center justify-center font-display">{(c.name || "?")[0]}</div>
                  <div><p className="font-medium">{c.name}</p><p className="text-xs text-stone-500">{c.target_role || "—"}</p></div>
                </div>
                <div className="text-right"><ScorePill score={c.wishire_score} /><p className="text-[10px] font-mono uppercase text-stone-400">score</p></div>
              </div>
              {c.skills?.length > 0 && (
                <div className="mt-4 flex flex-wrap gap-1.5">
                  {c.skills.slice(0, 5).map((s) => <span key={s} className="px-2 py-0.5 rounded-full bg-stone-100 text-stone-600 text-xs">{s}</span>)}
                </div>
              )}
              <button onClick={() => setSubmitFor(c)} className="btn-primary text-sm w-full justify-center mt-5"><Send size={14} /> Submit to client</button>
            </div>
          ))}
        </div>
      )}

      {submitFor && (
        <SubmitModal candidate={submitFor} clients={clients} onClose={() => setSubmitFor(null)} />
      )}
    </div>
  );
}

function SubmitModal({ candidate, clients, onClose }) {
  const [clientId, setClientId] = useState(clients[0]?.client_id || "");
  const [role, setRole] = useState(candidate.target_role || "");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  const submit = async () => {
    if (!clientId) { setErr("Add a client first."); return; }
    if (!role.trim()) { setErr("Role is required."); return; }
    setBusy(true); setErr("");
    try { await submitCandidate({ client_id: clientId, candidate_id: candidate.user_id, role: role.trim(), note: note.trim() || undefined }); setDone(true); }
    catch (e) { setErr(e?.response?.data?.detail || "Could not submit."); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="w-full max-w-md bg-[#FDFBF7] rounded-3xl overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-stone-200">
          <h2 className="font-display text-2xl">Submit candidate</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg text-stone-400 hover:bg-stone-100"><X size={18} /></button>
        </div>
        {done ? (
          <div className="p-8 text-center">
            <div className="inline-flex p-3 rounded-2xl bg-emerald-100 text-emerald-700 mb-3"><CheckCircle2 size={22} /></div>
            <p className="font-display text-xl">{candidate.name} submitted ✦</p>
            <p className="text-sm text-stone-600 mt-1">With a Wishire Score of {candidate.wishire_score}. Track it in Submissions.</p>
            <button onClick={onClose} className="btn-primary mt-5">Done</button>
          </div>
        ) : (
          <div className="px-6 py-5 space-y-4">
            <div className="flex items-center gap-3 rounded-2xl bg-white border border-stone-200 p-3">
              <div className="w-9 h-9 rounded-full bg-amber-100 text-amber-800 flex items-center justify-center font-display">{(candidate.name || "?")[0]}</div>
              <div className="flex-1"><p className="font-medium text-sm">{candidate.name}</p><p className="text-xs text-stone-500">Score {candidate.wishire_score}</p></div>
            </div>
            <div>
              <label className="overline">Client *</label>
              {clients.length === 0 ? (
                <p className="mt-1 text-sm text-red-700">No clients yet — add one in the Clients tab first.</p>
              ) : (
                <select value={clientId} onChange={(e) => setClientId(e.target.value)} className="mt-1.5 w-full px-4 py-2.5 rounded-xl bg-white border border-stone-300 text-sm">
                  {clients.map((c) => <option key={c.client_id} value={c.client_id}>{c.name}</option>)}
                </select>
              )}
            </div>
            <div><label className="overline">Role *</label><input value={role} onChange={(e) => setRole(e.target.value)} placeholder="Senior Backend Engineer" className="mt-1.5 w-full px-4 py-2.5 rounded-xl bg-white border border-stone-300 text-sm focus:outline-none focus:border-stone-900" /></div>
            <div><label className="overline">Note to client</label><textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Why they're a great fit…" className="mt-1.5 w-full px-4 py-2.5 rounded-xl bg-white border border-stone-300 text-sm focus:outline-none focus:border-stone-900" /></div>
            {err && <p className="text-sm text-red-700">{err}</p>}
            <button onClick={submit} disabled={busy || clients.length === 0} className="btn-primary w-full justify-center disabled:opacity-60">{busy ? <Loader2 size={15} className="animate-spin" /> : <>Submit to client <Send size={15} /></>}</button>
          </div>
        )}
      </div>
    </div>
  );
}

function CopyLink({ token }) {
  const [copied, setCopied] = useState(false);
  if (!token) return <span className="text-xs text-stone-400">—</span>;
  const url = `${window.location.origin}/s/${token}`;
  const copy = () => {
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };
  return (
    <div className="flex items-center gap-1.5">
      <button onClick={copy} title="Copy client dossier link"
        className={`inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full border transition-colors ${copied ? "border-emerald-300 text-emerald-700 bg-emerald-50" : "border-stone-300 text-stone-600 hover:border-stone-900"}`}>
        {copied ? <><Check size={11} /> Copied</> : <><Copy size={11} /> Copy</>}
      </button>
      <a href={url} target="_blank" rel="noreferrer" title="Open dossier" className="p-1 text-stone-400 hover:text-stone-900"><Link2 size={13} /></a>
    </div>
  );
}

/* ─────────── Open platform jobs (posted by companies) ─────────── */
export function AgencyJobs() {
  const [jobs, setJobs] = useState(null);
  const [talent, setTalent] = useState([]);
  const [submitFor, setSubmitFor] = useState(null); // job being submitted to
  const load = () => agencyOpenJobs().then(({ data }) => setJobs(data)).catch(() => setJobs([]));
  useEffect(() => { load(); agencyTalent().then(({ data }) => setTalent(data)).catch(() => {}); }, []);

  return (
    <div className="container-x py-10 md:py-14">
      <span className="overline">Open jobs</span>
      <h1 className="font-display text-4xl md:text-5xl mt-2">Jobs companies are hiring for — right now.</h1>
      <p className="text-stone-500 mt-2 max-w-lg">Submit your candidates straight into the company&apos;s pipeline. Each submission costs 1 credit and carries the candidate&apos;s verified Wishire Score.</p>

      {jobs === null ? <p className="text-stone-500 mt-8">Loading…</p> : jobs.length === 0 ? (
        <div className="mt-8 rounded-3xl border border-dashed border-stone-300 bg-[#F5F3ED] p-12 text-center text-stone-500">No open jobs on the platform right now.</div>
      ) : (
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {jobs.map((j) => (
            <div key={j.job_id} className="rounded-3xl border border-stone-200 bg-white p-6 flex flex-col">
              <p className="text-xs font-mono uppercase tracking-widest text-stone-400">{j.company_name || "Company"}</p>
              <h3 className="font-display text-2xl mt-1 leading-tight">{j.title}</h3>
              <p className="text-sm text-stone-500 mt-1 flex items-center gap-1.5">
                {j.target_role || "—"}{(j.location || j.remote) && <span className="inline-flex items-center gap-1 text-stone-400"><MapPin size={11} /> {j.remote ? "Remote" : j.location}</span>}
              </p>
              <div className="mt-4 flex items-center justify-between pt-4 border-t border-stone-100">
                <span className="text-xs text-stone-400 font-mono">{j.my_submissions > 0 ? `${j.my_submissions} submitted by you` : "No submissions yet"}</span>
                <button onClick={() => setSubmitFor(j)} data-testid={`ag-job-submit-${j.job_id}`}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-stone-900 text-white text-sm font-medium hover:bg-[#C2410C] transition-colors">
                  <Send size={13} /> Submit
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {submitFor && <JobSubmitModal job={submitFor} talent={talent} onClose={() => setSubmitFor(null)} onDone={() => { setSubmitFor(null); load(); }} />}
    </div>
  );
}

function JobSubmitModal({ job, talent, onClose, onDone }) {
  const [candidateId, setCandidateId] = useState(talent[0]?.user_id || "");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");
  const cand = talent.find((t) => t.user_id === candidateId);

  const submit = async () => {
    if (!candidateId) { setErr("Pick a candidate (browse Talent to see who's available)."); return; }
    setBusy(true); setErr("");
    try { await submitToJob(job.job_id, { candidate_id: candidateId, note: note.trim() || undefined }); setDone(true); }
    catch (e) { setErr(e?.response?.data?.detail || "Could not submit."); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="w-full max-w-md bg-[#FDFBF7] rounded-3xl overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-stone-200">
          <div><h2 className="font-display text-2xl">Submit to job</h2><p className="text-xs text-stone-500">{job.title} · {job.company_name}</p></div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-stone-400 hover:bg-stone-100"><X size={18} /></button>
        </div>
        {done ? (
          <div className="p-8 text-center" data-testid="ag-job-submit-done">
            <div className="inline-flex p-3 rounded-2xl bg-emerald-100 text-emerald-700 mb-3"><CheckCircle2 size={22} /></div>
            <p className="font-display text-xl">Submitted to {job.company_name} ✦</p>
            <p className="text-sm text-stone-600 mt-1">{cand?.name} is now in their applicant pipeline, tagged as your submission. Track the company&apos;s decision in Submissions.</p>
            <button onClick={onDone} className="btn-primary mt-5">Done</button>
          </div>
        ) : (
          <div className="px-6 py-5 space-y-4">
            <div>
              <label className="overline">Candidate *</label>
              {talent.length === 0 ? (
                <p className="mt-1 text-sm text-red-700">No scored candidates available — check the Talent tab (paid plans only).</p>
              ) : (
                <select value={candidateId} onChange={(e) => setCandidateId(e.target.value)} data-testid="ag-job-candidate" className="mt-1.5 w-full px-4 py-2.5 rounded-xl bg-white border border-stone-300 text-sm">
                  {talent.map((t) => <option key={t.user_id} value={t.user_id}>{t.name} · score {t.wishire_score}</option>)}
                </select>
              )}
            </div>
            <div><label className="overline">Note to the company</label><textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Why they're a great fit…" className="mt-1.5 w-full px-4 py-2.5 rounded-xl bg-white border border-stone-300 text-sm focus:outline-none focus:border-stone-900" /></div>
            {err && <p className="text-sm text-red-700">{err}</p>}
            <button onClick={submit} disabled={busy || talent.length === 0} data-testid="ag-job-submit-confirm" className="btn-primary w-full justify-center disabled:opacity-60">
              {busy ? <Loader2 size={15} className="animate-spin" /> : <>Submit (1 credit) <Send size={15} /></>}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/* ─────────── Submissions pipeline ─────────── */
export function AgencySubmissions() {
  const [rows, setRows] = useState(null);
  const load = () => listSubmissions().then(({ data }) => setRows(data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const move = async (s, status) => {
    setRows((arr) => arr.map((x) => (x.submission_id === s.submission_id ? { ...x, status } : x)));
    try { await updateSubmission(s.submission_id, status); } catch (_) { load(); }
  };

  return (
    <div className="container-x py-10 md:py-14">
      <span className="overline">Submissions</span>
      <h1 className="font-display text-4xl md:text-5xl mt-2">Your candidate pipeline.</h1>

      {rows === null ? <p className="text-stone-500 mt-8">Loading…</p> : rows.length === 0 ? (
        <div className="mt-8 rounded-3xl border border-dashed border-stone-300 bg-[#F5F3ED] p-12 text-center text-stone-500">No submissions yet. Browse Talent and submit candidates to your clients.</div>
      ) : (
        <div className="mt-8 overflow-x-auto rounded-2xl border border-stone-200 bg-white">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-stone-100 text-xs font-mono uppercase tracking-widest text-stone-400">
              {["Candidate", "Client", "Role", "Score", "Status", "Client link", ""].map((h) => <th key={h} className="text-left px-4 py-3 font-normal">{h}</th>)}
            </tr></thead>
            <tbody>
              {rows.map((s) => (
                <tr key={s.submission_id} className="border-b border-stone-50 hover:bg-stone-50">
                  <td className="px-4 py-3 font-medium text-stone-900">{s.candidate_name}</td>
                  <td className="px-4 py-3 text-stone-600">{s.client_name}</td>
                  <td className="px-4 py-3 text-stone-600">{s.role}</td>
                  <td className="px-4 py-3"><ScorePill score={s.wishire_score} /></td>
                  <td className="px-4 py-3"><span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-semibold ${STAGE_STYLE[s.status] || STAGE_STYLE.submitted}`}>{s.status}</span></td>
                  <td className="px-4 py-3"><CopyLink token={s.share_token} /></td>
                  <td className="px-4 py-3">
                    {s.via_platform_job ? (
                      <span className="text-xs text-stone-400" title="This went to a company's Wishire job — status follows their pipeline decision.">via Wishire job</span>
                    ) : (
                      <select value={s.status} onChange={(e) => move(s, e.target.value)} className="text-xs px-2 py-1 rounded-full border border-stone-300 bg-white cursor-pointer">
                        {STAGES.map((st) => <option key={st} value={st}>{st}</option>)}
                      </select>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
